import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService } from '../../shared/services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, interval } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ConsentData, ConsentModalComponent } from '../../shared/components/consent-modal/consent-modal.component';
import { exportRecordsPdf } from '../../shared/utils/pdf-export';
import { exportRecordsExcel } from '../../shared/utils/excel-export';
import { ConsentFlowService } from '../../shared/services/consent-flow.service';

interface TopTelephoneNumber {
  number?: string;
  lastUpdatedDate?: string | null;
  confidencePct?: number | null;
}

interface RequestProfile {
  firstName?: string | null;
  surname?: string | null;
  idNumber?: string | null;
  birthDate?: string | null;
  gender?: string | null;
  title?: string | null;
  homeAddress?: string | null;
  topTelephoneNumbers?: TopTelephoneNumber[];
  enquiryDate?: string | null;
  updatedAt?: any;
}

interface AddressTableRow {
  fullName: string | null;
  houseNumber: string | null;
  street: string | null;
  erfNumber: string | null;
  raw: Record<string, any>;
}

@Component({
  selector: 'app-data-viewer',
  templateUrl: './data-viewer.component.html',
  styleUrls: ['./data-viewer.component.css']
})
export class DataViewerComponent implements OnInit, OnDestroy {
  requestId: string = '';
  streetAddress: string = '';
  lookupType: 'ID' | 'ADDRESS' | 'TRUST' | 'DOVS' | null = null;
  backLabel: string = 'Back to search history';
  profile: RequestProfile | null = null;
  selectedProfile: RequestProfile | null = null;
  selectedAddressRow: AddressTableRow | null = null;
  addressExpanded: boolean = false;
  addressRowsDataSource = new MatTableDataSource<AddressTableRow>([]);
  addressDisplayedColumns: string[] = ['fullName', 'houseNumber', 'street', 'erfNumber', 'actions'];
  loading: boolean = false;
  waitingForRequest: boolean = false;
  requestStatus: 'IN_PROGRESS' | 'FAILED' | 'COMPLETE' | 'CANCELLED' | 'QUEUED' | null = null;
  requestResultCode: 'NO_MATCH' | null = null;
  requestErrorReason: string | null = null;
  requestErrorReasonCode: string | null = null;
  flagOutdatedLoading: boolean = false;
  flagOutdatedPending: boolean = false;
  dncLoading: boolean = false;
  downloadLoading: boolean = false;
  noContactInfoNoCredit: boolean = false;
  contactInfoMessage: string | null = null;
  loadDataErrorMessage: string | null = null;
  externalDataRetrievedAt: string | null = null;
  private backTarget:
    | 'history'
    | 'audit'
    | 'new-request'
    | 'user-request'
    | 'address-book'
    | 'user-address-book'
    | 'multi-search'
    | 'user-multi-search' = 'history';
  private backAuditUserId: string | null = null;
  private backAuditEmail: string | null = null;
  private backBatchId: string | null = null;
  private statusPollingSub: Subscription | null = null;
  private hasLoadedData = false;
  private resolvingConsentPrompt = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private consentFlowService: ConsentFlowService
  ) {}

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';
    this.loading = true;
    const from = this.route.snapshot.queryParamMap.get('from');
    const fromUserId = this.route.snapshot.queryParamMap.get('userId');
    const fromEmail = this.route.snapshot.queryParamMap.get('email');
    const fromBatchId = this.route.snapshot.queryParamMap.get('batchId');
    if (from === 'audit' && fromUserId) {
      this.backTarget = 'audit';
      this.backAuditUserId = fromUserId;
      this.backAuditEmail = fromEmail;
      this.backLabel = 'Back to audit log';
    } else if (from === 'new-request') {
      this.backTarget = 'new-request';
      this.backAuditUserId = null;
      this.backAuditEmail = null;
      this.backLabel = 'Back to search';
    } else if (from === 'user-request') {
      this.backTarget = 'user-request';
      this.backAuditUserId = null;
      this.backAuditEmail = null;
      this.backLabel = 'Back to search';
    } else if (from === 'address-book') {
      this.backTarget = 'address-book';
      this.backAuditUserId = null;
      this.backAuditEmail = null;
      this.backLabel = 'Back to address book';
    } else if (from === 'user-address-book') {
      this.backTarget = 'user-address-book';
      this.backAuditUserId = null;
      this.backAuditEmail = null;
      this.backLabel = 'Back to address book';
    } else if (from === 'multi-search' && fromBatchId) {
      this.backTarget = 'multi-search';
      this.backBatchId = fromBatchId;
      this.backAuditUserId = null;
      this.backAuditEmail = null;
      this.backLabel = 'Back to multi search';
    } else if (from === 'user-multi-search' && fromBatchId) {
      this.backTarget = 'user-multi-search';
      this.backBatchId = fromBatchId;
      this.backAuditUserId = null;
      this.backAuditEmail = null;
      this.backLabel = 'Back to multi search';
    } else {
      this.backTarget = 'history';
      this.backAuditUserId = null;
      this.backAuditEmail = null;
      this.backBatchId = null;
      this.backLabel = 'Back to search history';
    }
    this.refreshRequestStateAndData();
  }

  ngOnDestroy(): void {
    this.stopStatusPolling();
  }

  private refreshRequestStateAndData(): void {
    this.apiService.getRequestStatus(this.requestId).subscribe({
      next: (response) => {
        this.streetAddress = response.streetAddress || '';
        this.lookupType = response.lookupType || null;
        this.requestStatus = response.status;
        this.requestResultCode = response.resultCode || null;
        this.requestErrorReason = response.errorReason || null;
        this.requestErrorReasonCode = response.errorReasonCode || null;
        this.flagOutdatedPending = response.outdatedFlagPending === true;

        if (response.status === 'COMPLETE') {
          this.waitingForRequest = false;
          this.stopStatusPolling();
          if (!this.hasLoadedData) {
            this.loadData();
          } else {
            this.loading = false;
          }
          return;
        }

        if (response.status === 'QUEUED' || response.status === 'IN_PROGRESS') {
          this.loading = false;
          this.waitingForRequest = true;
          this.startStatusPolling();
          return;
        }

        this.loading = false;
        this.waitingForRequest = false;
        this.stopStatusPolling();
      },
      error: (error) => {
        this.loading = false;
        console.error('Failed to load request info:', error);
        const errorMessage = error?.error?.error?.message || 'Failed to load request status';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      }
    });
  }

  private startStatusPolling(): void {
    if (this.statusPollingSub) return;
    this.statusPollingSub = interval(4000)
      .pipe(switchMap(() => this.apiService.getRequestStatus(this.requestId)))
      .subscribe({
        next: (response) => {
          this.streetAddress = response.streetAddress || '';
          this.lookupType = response.lookupType || null;
          this.requestStatus = response.status;
          this.requestResultCode = response.resultCode || null;
          this.requestErrorReason = response.errorReason || null;
          this.requestErrorReasonCode = response.errorReasonCode || null;
          this.flagOutdatedPending = response.outdatedFlagPending === true;

          if (response.status === 'COMPLETE') {
            this.waitingForRequest = false;
            this.stopStatusPolling();
            if (!this.hasLoadedData) {
              this.loadData();
            }
            return;
          }

          if (response.status === 'QUEUED' || response.status === 'IN_PROGRESS') {
            this.waitingForRequest = true;
            return;
          }

          this.waitingForRequest = false;
          this.stopStatusPolling();
        },
        error: (error) => {
          console.error('Failed to poll request status:', error);
        }
      });
  }

  private stopStatusPolling(): void {
    this.statusPollingSub?.unsubscribe();
    this.statusPollingSub = null;
  }

  loadData(): void {
    this.loading = true;
    this.apiService.getRequestData(this.requestId).subscribe({
      next: async (response) => {
        this.loading = false;
        this.hasLoadedData = true;
        this.loadDataErrorMessage = response.resultCode === 'NO_MATCH'
          ? 'No records were found for this ID number.'
          : null;
        this.profile = response.profile || null;
        this.externalDataRetrievedAt = response.dataRetrievedAt || null;
        this.noContactInfoNoCredit = response.noContactInfoNoCredit === true;
        this.contactInfoMessage = response.contactInfoMessage || null;
        this.selectedProfile = null;
        this.selectedAddressRow = null;
        this.addressExpanded = false;

        if (this.lookupType === 'ADDRESS') {
          this.addressRowsDataSource.data = (response.data || []).map((row) => this.toAddressRow(row));
        } else {
          this.addressRowsDataSource.data = [];
        }
      },
      error: (error) => {
        this.loading = false;
        this.hasLoadedData = false;
        this.externalDataRetrievedAt = null;
        this.noContactInfoNoCredit = false;
        this.contactInfoMessage = null;
        const reasonCode = error?.error?.error?.reasonCode;
        this.loadDataErrorMessage =
          reasonCode === 'DNC_BLOCKED'
            ? 'This request is unavailable due to Do Not Contact status.'
            : null;
        if (this.consentFlowService.isConsentRequiredError(error)) {
          void this.promptForConsentAndRetryDataLoad();
          return;
        }
        const errorMessage = error?.error?.error?.message || 'Failed to load data';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      }
    });
  }

  formatRequestStatusLabel(): string {
    if (!this.requestStatus) return 'Queued';
    return this.requestStatus.replace(/_/g, ' ');
  }

  getRequestFailureMessage(): string {
    const fallback = 'No result is available for this request.';
    if (!this.requestErrorReason) return fallback;
    if (!this.requestErrorReasonCode) return this.requestErrorReason;
    return `${this.requestErrorReason} (Code: ${this.requestErrorReasonCode})`;
  }

  isAddressLookup(): boolean {
    return this.lookupType === 'ADDRESS';
  }

  isIdLookup(): boolean {
    return this.lookupType === 'ID';
  }

  showAddressTable(): boolean {
    return this.isAddressLookup() && !this.selectedAddressRow;
  }

  hasVisibleRequestData(): boolean {
    if (this.hasProfileData()) {
      return true;
    }
    return this.isAddressLookup() && this.addressRowsDataSource.data.length > 0;
  }

  viewAddressRow(row: AddressTableRow): void {
    this.selectedAddressRow = row;
    this.selectedProfile = this.toProfileFromAddressRow(row);
    this.addressExpanded = false;
  }

  closeAddressRowView(): void {
    this.selectedAddressRow = null;
    this.selectedProfile = null;
    this.addressExpanded = false;
  }

  getFullName(): string {
    const current = this.currentProfile();
    const first = current?.firstName?.trim() || '';
    const last = current?.surname?.trim() || '';
    if (!first && !last && this.selectedAddressRow?.fullName) return this.selectedAddressRow.fullName;
    const full = `${first} ${last}`.trim();
    return full || 'N/A';
  }

  hasProfileData(): boolean {
    const p = this.currentProfile();
    if (!p) return false;
    return Boolean(
      p.firstName ||
      p.surname ||
      p.idNumber ||
      p.birthDate ||
      p.gender ||
      p.title ||
      p.homeAddress ||
      (p.topTelephoneNumbers && p.topTelephoneNumbers.length > 0) ||
      p.enquiryDate
    );
  }

  showSyntheticPropertyRow(): boolean {
    return Boolean(this.isAddressLookup() && this.selectedAddressRow);
  }

  formatDateValue(value: any): string {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  }

  formatDateOnly(value: any): string {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  formatPhoneDisplay(number?: string | null): string {
    if (!number) return 'Unknown number';
    const digits = number.replace(/\D/g, '');
    if (!digits) return number;

    if (digits.length === 10) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }

    if (digits.length === 9) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }

    const grouped = digits.match(/.{1,3}/g);
    return grouped ? grouped.join(' ') : digits;
  }

  getPhoneTooltip(phone: TopTelephoneNumber): string {
    if (phone.lastUpdatedDate) {
      return `Last updated: ${this.formatDateOnly(phone.lastUpdatedDate)}`;
    }
    return 'Last updated: N/A';
  }

  getNoPhoneMessage(): string {
    if (this.noContactInfoNoCredit) {
      return this.contactInfoMessage || 'No contact information available. No credit charged';
    }
    return 'No telephone numbers available.';
  }

  hasLongAddress(): boolean {
    const address = this.currentProfile()?.homeAddress?.trim() || '';
    return address.length > 80;
  }

  getAddressDisplayValue(): string {
    const address = this.currentProfile()?.homeAddress?.trim() || '';
    if (!address) return 'N/A';
    if (this.addressExpanded || !this.hasLongAddress()) return address;
    return `${address.slice(0, 80)}...`;
  }

  getLastUpdatedDisplay(): string {
    const current = this.currentProfile();
    const value = this.selectedAddressRow
      ? current?.updatedAt || current?.enquiryDate
      : this.externalDataRetrievedAt || current?.updatedAt || current?.enquiryDate;
    if (!value) return 'N/A';
    return this.formatDateValue(value);
  }

  toggleAddressExpanded(): void {
    this.addressExpanded = !this.addressExpanded;
  }

  flagAsOutdated(): void {
    if (this.flagOutdatedLoading || this.flagOutdatedPending) return;
    const proceed = confirm(
      'Flag this record as outdated?\n\n' +
      'This will submit a request to fetch fresh data for this contact. ' +
      'The current view remains available while the refresh request is processed.'
    );
    if (!proceed) return;
    this.flagOutdatedLoading = true;
    this.apiService.flagRequestOutdated(this.requestId).subscribe({
      next: (response) => {
        this.flagOutdatedPending = response.mode === 'PENDING_APPROVAL';
        if (response.mode === 'DIRECT_REQUEST' && response.requestId) {
          this.flagOutdatedLoading = false;
          this.switchToRequest(response.requestId);
          return;
        }
        this.snackBar.open(
          this.flagOutdatedPending
            ? 'Record has been flagged as outdated and is now pending admin approval'
            : 'Record has been flagged as outdated, new data will be requested',
          'Close',
          { duration: 4000 }
        );
        this.flagOutdatedLoading = false;
      },
      error: (error) => {
        if (error?.error?.error?.reasonCode === 'OUTDATED_FLAG_ALREADY_PENDING') {
          this.flagOutdatedPending = true;
        }
        const errorMessage = error?.error?.error?.message || 'Failed to flag this record as outdated';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.flagOutdatedLoading = false;
      }
    });
  }

  markDoNotCall(): void {
    if (this.dncLoading) return;
    const proceed = confirm(
      'Mark this contact as Do Not Contact (DNC)?\n\n' +
      'This will block future contact actions for this contact in your company and ' +
      'access will be restricted after confirmation.'
    );
    if (!proceed) return;
    this.dncLoading = true;
    this.apiService.markRequestDnc(this.requestId).subscribe({
      next: () => {
        this.snackBar.open(
          'Contact marked as Do Not Contact for your company. View access is now blocked',
          'Close',
          { duration: 5000 }
        );
        this.dncLoading = false;
        this.navigateToBackTarget();
      },
      error: (error) => {
        const errorMessage = error?.error?.error?.message || 'Failed to mark contact as Do Not Contact';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.dncLoading = false;
      }
    });
  }

  async downloadPdf(): Promise<void> {
    await this.downloadRecord('pdf');
  }

  async downloadExcel(): Promise<void> {
    await this.downloadRecord('excel');
  }

  private async downloadRecord(format: 'pdf' | 'excel'): Promise<void> {
    if (this.downloadLoading) return;
    const current = this.currentProfile();
    if (!current) {
      this.snackBar.open('No profile data is available to export', 'Close', { duration: 4000 });
      return;
    }

    this.downloadLoading = true;
    try {
      const dialogRef = this.dialog.open(ConsentModalComponent, {
        width: '620px',
        disableClose: true,
        autoFocus: false,
        data: {
          title: 'Download Data Consent',
          body: [
            'By accepting, you confirm that this personal information will be processed only for the specific lawful purpose agreed to when your organisation subscribed to this service.',
            'In terms of POPIA principles, you acknowledge that once downloaded you become a responsible custodian of this data and must apply appropriate security safeguards, confidentiality, and access controls.',
          ],
          acceptLabel: 'Accept',
          rejectLabel: 'Reject',
        },
      });

      const consentResult = await firstValueFrom(dialogRef.afterClosed()) as ConsentData | undefined;
      if (!consentResult) {
        this.snackBar.open('Download consent was not completed', 'Close', { duration: 4000 });
        return;
      }

      await firstValueFrom(
        this.apiService.submitRequestDownloadConsent(this.requestId, { action: consentResult.action })
      );

      if (consentResult.action !== 'accept') {
        this.snackBar.open('Download cancelled because consent was rejected', 'Close', { duration: 4000 });
        return;
      }

      const exportRows = [
        {
          name: this.getFullName(),
          dob: current.birthDate ? this.formatDateOnly(current.birthDate) : 'N/A',
          idNumber: current.idNumber || 'N/A',
          ...this.formatTopPhonesForExport(current.topTelephoneNumbers || []),
        },
      ];
      if (format === 'pdf') {
        await exportRecordsPdf(
          'Record Download',
          exportRows,
          this.buildPdfFilename()
        );
      } else {
        exportRecordsExcel(exportRows, this.buildExcelFilename());
      }
    } catch (error: any) {
      const formatLabel = format === 'pdf' ? 'PDF' : 'Excel';
      const errorMessage = error?.error?.error?.message || `Failed to download ${formatLabel}`;
      this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
    } finally {
      this.downloadLoading = false;
    }
  }

  private formatTopPhonesForExport(phones: TopTelephoneNumber[]): {
    telephone1: string;
    telephone2: string;
    telephone3: string;
    telephone4: string;
    telephone5: string;
    telephone6: string;
    telephone7: string;
    telephone8: string;
  } {
    const values = phones
      .slice(0, 8)
      .map((phone) => this.normalizePhoneForExport(phone.number))
      .filter((value) => value && value !== 'Unknown number');
    return {
      telephone1: values[0] || 'N/A',
      telephone2: values[1] || 'N/A',
      telephone3: values[2] || 'N/A',
      telephone4: values[3] || 'N/A',
      telephone5: values[4] || 'N/A',
      telephone6: values[5] || 'N/A',
      telephone7: values[6] || 'N/A',
      telephone8: values[7] || 'N/A',
    };
  }

  private normalizePhoneForExport(number?: string | null): string {
    if (!number) return 'Unknown number';
    const compact = number.replace(/\s+/g, '').trim();
    return compact || 'Unknown number';
  }

  private buildPdfFilename(): string {
    const rawId = (this.requestId || 'record').replace(/[^a-zA-Z0-9_-]/g, '');
    const dateToken = new Date().toISOString().slice(0, 10);
    return `${rawId}-${dateToken}.pdf`;
  }

  private buildExcelFilename(): string {
    const rawId = (this.requestId || 'record').replace(/[^a-zA-Z0-9_-]/g, '');
    const dateToken = new Date().toISOString().slice(0, 10);
    return `${rawId}-${dateToken}.xlsx`;
  }

  private currentProfile(): RequestProfile | null {
    return this.selectedProfile || this.profile;
  }

  private firstNonEmptyString(values: any[]): string | null {
    for (const value of values) {
      if (typeof value !== 'string') continue;
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
    return null;
  }

  private toAddressRow(row: Record<string, any>): AddressTableRow {
    const houseValue = row['houseNumber'] ?? row['house_number'] ?? row['adrNo'] ?? row['ADR_NO'];
    const houseNumber =
      typeof houseValue === 'number'
        ? String(houseValue)
        : typeof houseValue === 'string' && houseValue.trim()
          ? houseValue.trim()
          : null;
    return {
      fullName: this.firstNonEmptyString([row['ownerFullName'], row['fullName'], row['name']]),
      houseNumber,
      street: this.firstNonEmptyString([row['streetName'], row['street'], row['street_name'], row['STR_NAME']]),
      erfNumber: this.firstNonEmptyString([row['erf'], row['erfNumber'], row['erf_number'], row['propertyNumber']]),
      raw: row,
    };
  }

  private toProfileFromAddressRow(row: AddressTableRow): RequestProfile {
    const raw = row.raw || {};
    const fullName = row.fullName || '';
    const [firstName, ...rest] = fullName.split(' ').filter(Boolean);
    const surname = rest.length ? rest.join(' ') : null;
    const houseStreet = [row.houseNumber, row.street].filter(Boolean).join(' ');
    const suburb =
      this.firstNonEmptyString([raw['suburbName'], raw['suburb'], raw['suburb_name']]) || null;
    return {
      firstName: firstName || null,
      surname,
      idNumber: this.firstNonEmptyString([raw['ownerIdNumber'], raw['idNumber'], raw['owner_id_number']]),
      title: null,
      birthDate: null,
      gender: null,
      enquiryDate: null,
      homeAddress: [houseStreet, suburb].filter(Boolean).join(', ') || null,
      topTelephoneNumbers: [],
      updatedAt: raw['dataAt'] || null,
    };
  }

  private navigateToBackTarget(): void {
    if (this.backTarget === 'audit' && this.backAuditUserId) {
      const queryParams = this.backAuditEmail ? { email: this.backAuditEmail } : {};
      void this.router.navigate(['/admin/users', this.backAuditUserId, 'audit-log'], {
        queryParams
      });
      return;
    }
    if (this.backTarget === 'new-request') {
      void this.router.navigate(['/admin/new-request']);
      return;
    }
    if (this.backTarget === 'user-request') {
      void this.router.navigate(['/user/data-request']);
      return;
    }
    if (this.backTarget === 'address-book') {
      void this.router.navigate(['/admin/address-book']);
      return;
    }
    if (this.backTarget === 'user-address-book') {
      void this.router.navigate(['/user/address-book']);
      return;
    }
    if (this.backTarget === 'multi-search' && this.backBatchId) {
      void this.router.navigate(['/admin/multi-search', this.backBatchId]);
      return;
    }
    if (this.backTarget === 'user-multi-search' && this.backBatchId) {
      void this.router.navigate(['/user/multi-search', this.backBatchId]);
      return;
    }
    void this.router.navigate(['/admin/history']);
  }

  private buildCurrentBackQueryParams(): Record<string, string> {
    if (this.backTarget === 'audit' && this.backAuditUserId) {
      const queryParams: Record<string, string> = {
        from: 'audit',
        userId: this.backAuditUserId,
      };
      if (this.backAuditEmail) {
        queryParams['email'] = this.backAuditEmail;
      }
      return queryParams;
    }
    if (this.backTarget === 'new-request') return { from: 'new-request' };
    if (this.backTarget === 'user-request') return { from: 'user-request' };
    if (this.backTarget === 'address-book') return { from: 'address-book' };
    if (this.backTarget === 'user-address-book') return { from: 'user-address-book' };
    if (this.backTarget === 'multi-search' && this.backBatchId) {
      return { from: 'multi-search', batchId: this.backBatchId };
    }
    if (this.backTarget === 'user-multi-search' && this.backBatchId) {
      return { from: 'user-multi-search', batchId: this.backBatchId };
    }
    return {};
  }

  private switchToRequest(nextRequestId: string): void {
    this.stopStatusPolling();
    this.requestId = nextRequestId;
    this.loading = true;
    this.waitingForRequest = false;
    this.requestStatus = null;
    this.requestResultCode = null;
    this.requestErrorReason = null;
    this.requestErrorReasonCode = null;
    this.hasLoadedData = false;
    this.profile = null;
    this.selectedProfile = null;
    this.selectedAddressRow = null;
    this.addressExpanded = false;
    this.addressRowsDataSource.data = [];
    this.externalDataRetrievedAt = null;
    this.noContactInfoNoCredit = false;
    this.contactInfoMessage = null;
    this.loadDataErrorMessage = null;
    void this.router.navigate(
      ['/admin/data', nextRequestId],
      { queryParams: this.buildCurrentBackQueryParams() }
    );
    this.refreshRequestStateAndData();
  }

  private async promptForConsentAndRetryDataLoad(): Promise<void> {
    if (this.resolvingConsentPrompt) return;
    this.resolvingConsentPrompt = true;

    try {
      this.loading = false;
      const consentAccepted = await this.consentFlowService.ensureConsentAccepted();
      if (!consentAccepted) {
        return;
      }
      this.loadData();
    } catch (error) {
      this.loading = false;
      console.error('Failed to resolve POPIA acknowledgement while viewing data:', error);
      this.snackBar.open('An error occurred while processing POPIA acknowledgement', 'Close', { duration: 5000 });
    } finally {
      this.resolvingConsentPrompt = false;
    }
  }

  goBack(): void {
    this.navigateToBackTarget();
  }
}


